import React, { useState, useCallback, ChangeEvent } from "react"
import styled from "styled-components"
import Loading from "src/app/admin/loading"
import { boxShadow } from "src/assets/styles"
import { color, font, useNotification } from "@smoothincorg/smooth-ui"
import { Wrapper } from "src/components/page-directory/Wrapper"
import { A } from "src/components/A"
import { Vercelist } from "../Vercelist/Vercelist"
import { StoreInList } from "src/hooks/useStoresSWR"
import { Modal } from "src/app/admin/_components/modal"
import { InputField } from "src/components/InputField"
import { Button } from "src/components/Button"
import { BorderedButton } from "src/components/Button"
import { isValidEmail } from "src/lib/utils/form"
import { useCreateStoreForDemo } from "src/features/stores/hooks/useCreateStoreForDemo/useCreateStoreForDemo"

type Props = {
  className?: string
  stores: StoreInList[]
  onStoreAdded?: () => void
}

const Component: React.VFC<Props> = (props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showSuccessScreen, setShowSuccessScreen] = useState(false)
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    password: "",
  })
  const [emailError, setEmailError] = useState("")
  const { showSnackbar } = useNotification()
  const { createStoreForDemo } = useCreateStoreForDemo()
  const [createdLabel, setCreatedLabel] = useState("")

  const validateEmail = useCallback((value: string) => {
    const { isValid, reason } = isValidEmail(value)

    if (!isValid) {
      const errorMessage =
        reason === "empty"
          ? "メールアドレスを入力してください"
          : "正しいメールアドレスを入力してください"
      setEmailError(errorMessage)
      return false
    }

    setEmailError("")
    return true
  }, [])

  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFormState((state) => ({
      ...state,
      name: e.target.value,
    }))
  }, [])

  const handleEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const sanitizedValue = e.target.value.replace(/\s+/g, "")

      setFormState((state) => ({
        ...state,
        email: sanitizedValue,
      }))

      validateEmail(sanitizedValue)
    },
    [validateEmail]
  )

  const handlePasswordChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFormState((state) => ({
      ...state,
      password: e.target.value,
    }))
  }, [])

  const handleSubmit = useCallback(async () => {
    try {
      showSnackbar("店舗を作成中です...", "info")
      const response = await createStoreForDemo({
        name: formState.name,
        email: formState.email,
        password: formState.password,
      })

      showSnackbar("店舗を作成しました", "success")
      setShowSuccessScreen(true)
      setCreatedLabel(response.store.label)
      props.onStoreAdded?.()
    } catch (e: any) {
      showSnackbar(
        `店舗の作成に失敗しました: ${e.response?.data?.error || "不明なエラーが発生しました"}`,
        "error"
      )
    }
  }, [formState, showSnackbar, createStoreForDemo, validateEmail, props])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    setShowSuccessScreen(false)
    setFormState({
      name: "",
      email: "",
      password: "",
    })
    setEmailError("")
  }, [])

  const handleSuccessClose = useCallback(() => {
    setIsModalOpen(false)
    setShowSuccessScreen(false)
    setFormState({
      name: "",
      email: "",
      password: "",
    })
    setEmailError("")
  }, [])

  const isSubmitDisabled =
    !formState.name.trim() || !isValidEmail(formState.email).isValid || !formState.password.trim()

  if (!props.stores) return <Loading />

  return (
    <Wrapper className={props.className}>
      <div className={`${props.className}-header`}>
        <Button
          onClick={() => setIsModalOpen(true)}
          backgroundColor="main"
          style={{ marginBottom: 24, padding: "12px 24px", fontSize: "1.4rem", fontWeight: "bold"}}
        >
          デモ店舗を追加
        </Button>
      </div>
      <ul className={`${props.className}-stores`}>
        {props.stores.map((store) => {
          return (
            <li key={store.id} className={`${props.className}-stores__item`}>
              <A href={`/admin/stores/${store.id}`}>
                <Vercelist
                  items={[
                    ["ID", store.id],
                    ["ストア名", store.name],
                    ["企業", store.companyName],
                    ["新メッセシステム", store.storeAuth?.effective_now ? "有効" : "無効"],
                  ]}
                />
              </A>
            </li>
          )
        })}
      </ul>

      {isModalOpen && (
        <Modal onClose={handleModalClose}>
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              background: "#fff",
              borderRadius: 8,
              padding: 32,
              minWidth: 600,
              maxWidth: 800,
              zIndex: 100,
            }}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <h2 style={{ fontWeight: "bold", fontSize: 20 }}>
                {showSuccessScreen ? "デモ店舗の作成をしました" : "デモ店舗を追加"}
              </h2>
              <button
                onClick={showSuccessScreen ? handleSuccessClose : handleModalClose}
                style={{
                  fontSize: 24,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: color.darkGray,
                }}
              >
                ×
              </button>
            </div>

            {showSuccessScreen ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <p
                  style={{
                    fontSize: "1.4rem",
                    color: color.darkGray,
                    marginBottom: 32,
                  }}
                >
                  店舗名: {formState.name}
                </p>
                <p
                  style={{
                    fontSize: "1.4rem",
                    color: color.darkGray,
                    marginBottom: 32,
                  }}
                >
                  ラベル: {createdLabel}
                </p>
                <p
                  style={{
                    fontSize: "1.4rem",
                    color: color.darkGray,
                    marginBottom: 32,
                  }}
                >
                  LPのURL: <a href={`https://lp.demo.smooth.jp/${createdLabel}`} target="_blank" rel="noopener noreferrer" style={{ color: color.main, textDecoration: "underline" }}>{`https://lp.demo.smooth.jp/${createdLabel}`}</a>
                </p>
                <p
                  style={{
                    fontSize: "1.4rem",
                    color: color.darkGray,
                    marginBottom: 12,
                  }}
                >
                  パートナーOPEのURL: <a href="https://partner.demo.smooth.jp/" target="_blank" rel="noopener noreferrer" style={{ color: color.main, textDecoration: "underline" }}>{`https://partner.demo.smooth.jp/`}</a>
                </p>
                <p
                  style={{
                    fontSize: "1.4rem",
                    color: color.darkGray,
                    marginBottom: 12,
                  }}
                >
                  ログインID: {formState.email}
                </p>
                <p
                  style={{
                    fontSize: "1.4rem",
                    color: color.darkGray,
                    marginBottom: 32,
                  }}
                >
                  パスワード: {formState.password}
                </p>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    onClick={handleSuccessClose}
                    backgroundColor="main"
                    style={{ padding: "12px 24px", fontSize: "1.4rem", fontWeight: "bold" }}
                  >
                    閉じる
                  </Button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleSubmit()
                }}
              >
                <div style={{ marginBottom: 20, display: "flex", gap: 4, justifyContent: "flex-start" }}>
                  <p style={{ fontSize: "1.4rem", fontWeight: "bold", width: "150px", display: "flex", alignItems: "center" }}>店舗名</p>
                  <input
                    type="text"
                    style={{ width: "360px", padding: "12px", borderRadius: "8px", border: "1px solid #e5e5e5", fontSize: "1.4rem" }}
                    value={formState.name}
                    onChange={handleNameChange}
                    placeholder="店舗名を入力"
                    required
                  />
                </div>

                <p style={{ fontSize: "1.4rem", color: color.darkGray, marginBottom: 20 }}>
                  ※メールアドレスとパスワードはパートナーOPEのログインに使用します
                </p>
                <div style={{ marginBottom: 20, display: "flex", gap: 4 }}>
                  <p style={{ fontSize: "1.4rem", fontWeight: "bold", width: "150px", display: "flex", alignItems: "center" }}>メールアドレス</p>
                  <input
                    type="email"
                    style={{ width: "360px", padding: "12px", borderRadius: "8px", border: "1px solid #e5e5e5", fontSize: "1.4rem" }}
                    value={formState.email}
                    onChange={handleEmailChange}
                    placeholder="info@smooth.jp"
                    required
                  />
                </div>

                <div style={{ marginBottom: 24, display: "flex", gap: 4, alignItems: "center" }}>
                  <p style={{ fontSize: "1.4rem", fontWeight: "bold", width: "150px", display: "flex", alignItems: "center" }}>パスワード</p>
                  <input
                    type="text"
                    style={{ width: "360px", padding: "12px", borderRadius: "8px", border: "1px solid #e5e5e5", fontSize: "1.4rem" }}
                    value={formState.password}
                    onChange={handlePasswordChange}
                    placeholder="パスワードを入力"
                    required
                  />
                  <button
                    type="button"
                    style={{ fontSize: "1.2rem", color: color.white, cursor: "pointer", width: "100px", height: "30px", borderRadius: "8px", fontWeight: "bold", backgroundColor: color.darkGray}}
                    onClick={() => {
                      setFormState((state) => ({
                        ...state,
                        password: Math.random().toString(36).substring(2, 15),
                      }))
                    }}
                  >
                    自動生成
                  </button>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                  <BorderedButton type="button" onClick={handleModalClose}>
                    キャンセル
                  </BorderedButton>
                  <Button type="submit" disabled={isSubmitDisabled} backgroundColor="main" style={{ padding: "12px 24px", fontSize: "1.4rem", fontWeight: "bold"}}>
                    作成
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Modal>
      )}
    </Wrapper>
  )
}

const StyledComponent = styled(Component)`
  padding: 60px 0;

  &-header {
    margin-bottom: 24px;
  }

  &-stores {
    &__item {
      margin-bottom: 12px;
      border-radius: 8px;
      width: 100%;
      ${boxShadow.deep};
      padding: 8px 18px;

      .vercelist {
        display: flex;

        > li[data-title='ID'] {
          width: 10%;
        }

        > li[data-title='ストア名'] {
          width: 30%;
        }

        > li[data-title='企業'] {
          width: 30%;
        }

        > li[data-title='提案数'] {
          width: 15%;
        }

        > li[data-title='従業員数'] {
          width: 15%;
        }
      }
    }
  }

  &-store-item {
    font-weight: bold;
    padding-right: 45px;
    white-space: pre-wrap;

    &__title {
      ${font(13)};
      display: block;
      color: ${color.darkGray};
    }

    &__content {
      ${font(15)};
      display: block;
    }
  }
`

export const Stores = StyledComponent
